// Shared invoice template for Call for Funds and Quittance emails
// Based on the invoice-template.html format
// COMPLIANT WITH FRENCH LAW (Article 242 nonies A du CGI)

// Issue date: 1st of the billed month when the quittance was generated in advance.
export function quittanceIssueDate(generatedAt: string, periodYyyymm?: string | null): string {
  const generated = new Date(generatedAt);
  const [y, m] = (periodYyyymm || '').split('-').map(Number);
  const periodStart = y && m ? new Date(y, m - 1, 1) : null;
  return (periodStart && generated < periodStart ? periodStart : generated).toLocaleDateString('fr-FR');
}

export interface InvoiceTemplateData {
  // Document info
  documentType: 'facture' | 'quittance' | 'appel';
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  periodLabel: string;
  isPaid?: boolean;
  paymentDate?: string;

  // Issuer (SCI) info
  issuerName: string;
  issuerAddress: string;
  issuerCity: string;
  issuerPostalCode: string;
  issuerSiret?: string;
  issuerVat?: string;
  issuerIban?: string;
  issuerBic?: string;
  issuerLogoUrl?: string;
  issuerCapital?: string;
  issuerRcs?: string;
  legalMentions?: string;

  // Client (Tenant) info
  clientDisplayName: string;
  clientAddress?: string;
  clientCity?: string;
  clientPostalCode?: string;
  clientSiret?: string;
  clientVat?: string;
  clientEmail?: string;
  isProfessional: boolean;

  // Property info
  propertyLabel: string;
  propertyAddress: string;

  // Amounts
  rentDue: number;
  chargesDue: number;
  vatRate?: number;
  
  // Line items (optional, for detailed invoices)
  lineItems?: Array<{
    label: string;
    annotation?: string | null;
    type: string;
    amountHT: number;
    vatRate: number;
    amountTTC: number;
  }>;

  // Annual indexation: note under the totals (the annex is sent as a separate attachment).
  indexationNote?: string;
}

function formatIban(iban: string): string {
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

function formatSiret(siret: string): string {
  // Format SIRET: XXX XXX XXX XXXXX
  const clean = siret.replace(/\s/g, '');
  if (clean.length === 14) {
    return clean.slice(0, 3) + ' ' + clean.slice(3, 6) + ' ' + clean.slice(6, 9) + ' ' + clean.slice(9);
  }
  return siret;
}

export function generateInvoiceHtml(data: InvoiceTemplateData): string {
  const hasItems = !!(data.lineItems && data.lineItems.length > 0);
  const depositAdj = hasItems ? data.lineItems!.filter(i => i.type === 'deposit').reduce((s, i) => s + i.amountHT, 0) : 0;
  const subTotal = (hasItems ? data.lineItems!.reduce((s, i) => s + i.amountHT, 0) : data.rentDue + data.chargesDue) - depositAdj;
  const vatAmount = hasItems
    ? data.lineItems!.reduce((s, i) => s + (i.amountTTC - i.amountHT), 0)
    : (data.vatRate ? subTotal * (data.vatRate / 100) : 0);
  const total = subTotal + vatAmount + depositAdj;
  const itemRates = hasItems ? [...new Set(data.lineItems!.filter(i => i.vatRate > 0).map(i => i.vatRate))] : [];
  const vatLabel = hasItems
    ? (itemRates.length ? ` (${itemRates.join('% / ')}%)` : '')
    : (data.vatRate ? ` (${data.vatRate}%)` : '');

  const issuerAddressLine = [data.issuerAddress, data.issuerPostalCode, data.issuerCity]
    .filter(Boolean)
    .join(', ');

  const clientAddressLine = [data.clientAddress, data.clientPostalCode, data.clientCity]
    .filter(Boolean)
    .join(', ');

  const formattedIban = data.issuerIban ? formatIban(data.issuerIban) : '';
  const formattedSiret = data.issuerSiret ? formatSiret(data.issuerSiret) : '';

  // Document titles per French law
  const documentTitles: Record<string, string> = {
    'facture': 'FACTURE',
    'quittance': 'QUITTANCE DE LOYER',
    'appel': "AVIS D'ÉCHÉANCE"
  };
  const documentTitle = documentTitles[data.documentType] || 'FACTURE';
  

  // Line items rows
  let lineItemsHtml = '';
  if (data.lineItems && data.lineItems.length > 0) {
    lineItemsHtml = data.lineItems.map(item => `
      <tr>
        <td>${item.label}${item.annotation ? `<br/><span style="font-size:11px;color:#1f4e79;font-style:italic">${item.annotation}</span>` : ''}${data.propertyAddress ? `<br/><span style="font-size:11px;color:#6b7280">${data.propertyAddress}</span>` : ''}</td>
        <td class="num">1</td>
        <td class="num">${item.amountHT.toFixed(2)}</td>
        <td class="num">${item.vatRate > 0 ? item.vatRate + '%' : '-'}</td>
        <td class="num">${item.amountHT.toFixed(2)}</td>
      </tr>
    `).join('');
  } else {
    lineItemsHtml = `
      <tr>
        <td>Loyer — ${data.propertyLabel}${data.propertyAddress ? `<br/><span style="font-size:11px;color:#6b7280">${data.propertyAddress}</span>` : ''}</td>
        <td class="num">1</td>
        <td class="num">${data.rentDue.toFixed(2)}</td>
        <td class="num">${data.vatRate ? data.vatRate + '%' : '-'}</td>
        <td class="num">${data.rentDue.toFixed(2)}</td>
      </tr>
      ${data.chargesDue > 0 ? `
      <tr>
        <td>Charges locatives — ${data.propertyLabel}${data.propertyAddress ? `<br/><span style="font-size:11px;color:#6b7280">${data.propertyAddress}</span>` : ''}</td>
        <td class="num">1</td>
        <td class="num">${data.chargesDue.toFixed(2)}</td>
        <td class="num">${data.vatRate ? data.vatRate + '%' : '-'}</td>
        <td class="num">${data.chargesDue.toFixed(2)}</td>
      </tr>
      ` : ''}
    `;
  }

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>${documentTitle} ${data.invoiceNumber}</title>
  <style>
    @page { 
      size: A4; 
      margin: 15mm; 
    }
    * { margin:0; padding:0; box-sizing:border-box; }
    body { 
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; 
      font-size: 11px; 
      color:#1f2937; 
      padding: 20px 30px;
      background: white;
      line-height: 1.4;
    }
    .container { max-width: 100%; margin: 0 auto; }
    
    /* Header */
    .header { 
      display: flex; 
      justify-content: space-between; 
      align-items: flex-start;
      padding-bottom: 20px; 
      border-bottom: 3px solid #1f2937; 
      margin-bottom: 20px;
    }
    .issuer { width: 55%; }
    .issuer .logo { max-height: 60px; margin-bottom: 12px; }
    .issuer .name { font-size: 18px; font-weight: 700; color: #111827; margin-bottom: 8px; }
    .issuer .meta { color: #4b5563; font-size: 10px; line-height: 1.5; }
    .issuer .meta div { margin-bottom: 2px; }
    
    .doc-info { width: 40%; text-align: right; }
    .doc-info .type { 
      font-size: 20px; 
      font-weight: 800; 
      letter-spacing: 1px; 
      color: #111827;
      margin-bottom: 12px;
    }
    .doc-info .details { font-size: 11px; color: #374151; line-height: 1.6; }
    .doc-info .details strong { color: #111827; }
    
    /* Parties Grid */
    .parties { 
      display: flex; 
      justify-content: space-between; 
      gap: 20px; 
      margin-bottom: 25px;
    }
    .party-box { 
      width: 48%; 
      background: #f9fafb;
      border: 1px solid #e5e7eb; 
      border-radius: 6px; 
      padding: 16px; 
    }
    .party-box h3 { 
      font-size: 9px; 
      letter-spacing: 1.5px; 
      text-transform: uppercase; 
      color: #6b7280; 
      margin-bottom: 10px;
      font-weight: 600;
    }
    .party-box .name { font-size: 13px; font-weight: 700; color: #111827; margin-bottom: 6px; }
    .party-box .detail { font-size: 10px; color: #4b5563; margin-bottom: 3px; }
    
    /* Property Box */
    .property-box {
      background: #fef3c7;
      border: 1px solid #fcd34d;
      border-radius: 6px;
      padding: 12px 16px;
      margin-bottom: 20px;
    }
    .property-box .label { font-size: 9px; text-transform: uppercase; color: #92400e; font-weight: 600; letter-spacing: 1px; }
    .property-box .value { font-size: 12px; font-weight: 600; color: #78350f; margin-top: 4px; }
    
    /* Table */
    table.items { 
      width: 100%; 
      border-collapse: collapse; 
      margin-bottom: 20px;
    }
    table.items th { 
      text-align: left; 
      font-size: 9px; 
      text-transform: uppercase; 
      letter-spacing: 0.5px; 
      color: #6b7280; 
      padding: 10px 12px; 
      background: #f3f4f6;
      border-bottom: 2px solid #d1d5db;
      font-weight: 600;
    }
    table.items td { 
      padding: 12px; 
      border-bottom: 1px solid #e5e7eb; 
      color: #374151; 
      font-size: 11px;
    }
    table.items .num, table.items th.num { text-align: right; }
    
    /* Totals */
    .totals-wrapper { 
      display: flex; 
      justify-content: flex-end; 
      margin-bottom: 20px;
    }
    table.totals { width: 280px; }
    table.totals td { 
      padding: 8px 12px; 
      font-size: 11px;
    }
    table.totals .label { color: #6b7280; }
    table.totals .value { text-align: right; font-weight: 500; }
    table.totals .grand td { 
      font-size: 14px; 
      font-weight: 700; 
      color: #111827; 
      border-top: 2px solid #111827; 
      padding-top: 12px;
    }
    table.totals .grand.paid td { color: #16a34a; }
    
    /* Payment Box */
    .payment-box { 
      background: #f0f9ff;
      border: 1px solid #bae6fd;
      border-radius: 6px; 
      padding: 16px; 
      margin-bottom: 20px;
    }
    .payment-box.paid {
      background: #f0fdf4;
      border-color: #86efac;
    }
    .payment-box h4 { 
      font-size: 11px; 
      font-weight: 700; 
      color: #0369a1; 
      margin-bottom: 8px;
    }
    .payment-box.paid h4 { color: #15803d; }
    .payment-box p { font-size: 10px; color: #334155; line-height: 1.5; margin: 0; }
    .payment-box .iban { 
      background: white; 
      padding: 8px 12px; 
      border-radius: 4px; 
      font-family: monospace; 
      font-size: 11px; 
      margin-top: 10px;
      border: 1px solid #e2e8f0;
    }
    
    /* Footer */
    .footer { 
      margin-top: 30px;
      padding-top: 15px; 
      border-top: 1px solid #e5e7eb; 
      font-size: 9px; 
      color: #6b7280; 
      line-height: 1.5;
    }
    .footer .legal { margin-bottom: 10px; }
    .footer .mentions { 
      background: #f9fafb; 
      padding: 10px; 
      border-radius: 4px;
      font-size: 8px;
    }
  </style>
</head>
<body>
  <div class="container">
    <!-- Header -->
    <div class="header">
      <div class="issuer">
        ${data.issuerLogoUrl ? `<img class="logo" src="${data.issuerLogoUrl}" alt="Logo" />` : ''}
        <div class="name">${data.issuerName}</div>
        <div class="meta">
          <div>${issuerAddressLine}</div>
          ${formattedSiret ? `<div><strong>SIRET :</strong> ${formattedSiret}</div>` : ''}
          ${data.issuerRcs ? `<div><strong>RCS :</strong> ${data.issuerRcs}</div>` : ''}
          ${data.issuerCapital ? `<div><strong>Capital :</strong> ${data.issuerCapital}</div>` : ''}
          ${data.issuerVat ? `<div><strong>N° TVA Intracommunautaire :</strong> ${data.issuerVat}</div>` : ''}
        </div>
      </div>
      
      <div class="doc-info">
        <div class="type">${documentTitle}</div>
        <div class="details">
          <div><strong>N° Document :</strong> ${data.invoiceNumber}</div>
          <div><strong>Date d'émission :</strong> ${data.issueDate}</div>
          ${data.dueDate ? `<div><strong>Date d'échéance :</strong> ${data.dueDate}</div>` : ''}
          <div><strong>Période :</strong> ${data.periodLabel}</div>
        </div>
      </div>
    </div>
    
    <!-- Parties -->
    <div class="parties">
      <div class="party-box">
        <h3>Émetteur / Bailleur</h3>
        <div class="name">${data.issuerName}</div>
        <div class="detail">${issuerAddressLine}</div>
        ${formattedSiret ? `<div class="detail">SIRET : ${formattedSiret}</div>` : ''}
        ${data.issuerVat ? `<div class="detail">N° TVA Intracommunautaire : ${data.issuerVat}</div>` : ''}
      </div>
      
      <div class="party-box">
        <h3>Destinataire / Locataire</h3>
        <div class="name">${data.clientDisplayName}</div>
        ${clientAddressLine ? `<div class="detail">${clientAddressLine}</div>` : ''}
        ${data.isProfessional && data.clientSiret ? `<div class="detail">SIRET : ${formatSiret(data.clientSiret)}</div>` : ''}
        ${data.isProfessional && data.clientVat ? `<div class="detail">N° TVA : ${data.clientVat}</div>` : ''}
        ${data.clientEmail ? `<div class="detail">${data.clientEmail}</div>` : ''}
      </div>
    </div>
    
    <!-- Property -->
    <div class="property-box">
      <div class="label">Bien concerné</div>
      <div class="value">${data.propertyLabel}${data.propertyAddress ? ` — ${data.propertyAddress}` : ''}</div>
    </div>
    
    <!-- Items Table -->
    <table class="items">
      <thead>
        <tr>
          <th>Désignation</th>
          <th class="num">Qté</th>
          <th class="num">P.U. HT (€)</th>
          <th class="num">Taux de TVA</th>
          <th class="num">Total HT (€)</th>
        </tr>
      </thead>
      <tbody>
        ${lineItemsHtml}
      </tbody>
    </table>
    
    <!-- Totals -->
    <div class="totals-wrapper">
      <table class="totals">
        <tr>
          <td class="label">${depositAdj ? 'Total loyers HT' : 'Sous-total HT'}</td>
          <td class="value">${subTotal.toFixed(2)} €</td>
        </tr>
        <tr>
          <td class="label">TVA${vatLabel}</td>
          <td class="value">${vatAmount.toFixed(2)} €</td>
        </tr>
        ${depositAdj ? `
        <tr>
          <td class="label">Total loyers TTC</td>
          <td class="value">${(subTotal + vatAmount).toFixed(2)} €</td>
        </tr>
        <tr>
          <td class="label">Ajustement dépôt de garantie</td>
          <td class="value">${depositAdj.toFixed(2)} €</td>
        </tr>` : ''}
        <tr class="grand">
          <td>${depositAdj ? 'Total à régler' : 'Total TTC à régler'}</td>
          <td class="value">${total.toFixed(2)} €</td>
        </tr>
      </table>
    </div>
    ${data.indexationNote ? `<div style="background:#eaf1f8;border-left:3px solid #1f4e79;padding:12px 14px;margin-bottom:20px;font-size:11px;color:#1c2430">${data.indexationNote}</div>` : ''}
    
    <!-- Payment Box -->
    <div class="payment-box">
      <h4>Modalités de règlement</h4>
      <p>
        Montant à régler : <strong>${total.toFixed(2)} €</strong> au titre du loyer et des charges pour la période de <strong>${data.periodLabel}</strong>${data.dueDate ? `, avant le <strong>${data.dueDate}</strong>` : ''}.
        ${data.issuerIban ? `
          <br/>Paiement par virement bancaire :
          <div class="iban">
            <strong>Titulaire :</strong> ${data.issuerName}<br/>
            <strong>IBAN :</strong> ${formattedIban}${data.issuerBic ? `<br/><strong>BIC :</strong> ${data.issuerBic}` : ''}
          </div>
        ` : ''}
        <br/>Nous vous remercions par avance pour votre règlement.
      </p>
    </div>
    
    <!-- Footer -->
    <div class="footer">
      <div class="mentions">
        <strong>Document émis par :</strong> ${data.issuerName}
        ${formattedSiret ? ` — SIRET : ${formattedSiret}` : ''}
        ${data.issuerVat ? ` — TVA : ${data.issuerVat}` : ''}
        ${data.issuerRcs ? ` — ${data.issuerRcs}` : ''}
        — Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  </div>
</body>
</html>`;
}

// Email template for Call for Funds
export function generateCallForFundsEmailHtml(data: {
  callNumber: string;
  formattedPeriod: string;
  dueDate: string;
  tenantName: string;
  isProfessional: boolean;
  tenantSiret?: string;
  tenantVatNumber?: string;
  propertyName: string;
  propertyAddress: string;
  rentDue: number;
  chargesDue: number;
  totalDue: number;
  sciName: string;
  sciAddress: string;
  sciSiret?: string;
  sciIban?: string;
  sciBic?: string;
  signatureName?: string;
}): string {
  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Quittance de loyer - ${data.formattedPeriod}</title>
</head>
<body style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f5f5f5;">
  <div style="background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
    <div style="background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%); color: white; padding: 30px;">
      <h1 style="margin: 0; font-size: 24px; font-weight: 600;">Quittance de loyer</h1>
      <p style="margin: 8px 0 0 0; opacity: 0.9; font-size: 16px;">${data.formattedPeriod}</p>
    </div>

    <div style="padding: 30px;">
      <p style="margin: 0 0 20px 0; font-size: 15px;">Bonjour <strong>${data.tenantName}</strong>,</p>

      <p style="margin: 0 0 20px 0; color: #4b5563; font-size: 14px;">
        Veuillez trouver ci-joint votre quittance de loyer pour la période de <strong>${data.formattedPeriod}</strong>, concernant le bien <strong>${data.propertyName}</strong>${data.propertyAddress ? ` situé au <strong>${data.propertyAddress}</strong>` : ''}.
      </p>

      <p style="margin: 0 0 20px 0; color: #4b5563; font-size: 14px;">
        Nous vous remercions de bien vouloir procéder au règlement avant le <strong>${data.dueDate}</strong>.
      </p>

      <div style="background: #fef3c7; border: 1px solid #fcd34d; padding: 16px; border-radius: 8px; margin: 20px 0;">
        <p style="margin: 0; font-size: 13px; color: #92400e;">
          <strong>📎 Pièce jointe :</strong> Votre quittance de loyer est jointe à cet email au format PDF. Merci de la consulter pour le détail des montants et les coordonnées de règlement.
        </p>
      </div>

      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0;">
        <p style="margin: 0; font-weight: 600; color: #1f2937;">${data.sciName}</p>
        ${data.sciAddress ? `<p style="color: #6b7280; font-size: 13px; margin: 5px 0;">${data.sciAddress}</p>` : ""}
        ${data.signatureName ? `<p style="margin-top: 15px; font-style: italic; color: #4b5563;">Pour ${data.sciName},<br/><strong>${data.signatureName}</strong></p>` : ""}
      </div>
    </div>

    <div style="background: #1f2937; color: #9ca3af; padding: 20px; font-size: 11px; text-align: center;">
      <p style="margin: 0 0 5px 0;">Document généré automatiquement le ${new Date().toLocaleDateString("fr-FR")}</p>
    </div>
  </div>
</body>
</html>`;
}

// Email template for Quittance (sent before payment is received)
export function generateQuittanceEmailHtml(data: {
  receiptNumber: string;
  formattedPeriod: string;
  paymentDate?: string;
  dueDate?: string;
  sciIban?: string;
  sciBic?: string;
  tenantName: string;
  isProfessional: boolean;
  tenantSiret?: string;
  tenantVatNumber?: string;
  propertyName: string;
  propertyAddress: string;
  rentDue: number;
  chargesDue: number;
  totalDue: number;
  depositAdjustment?: number;
  hasIndexationAnnex?: boolean;
  sciName: string;
  sciAddress: string;
  sciSiret?: string;
  signatureName?: string;
  issueDate?: string;
}): string {
  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Quittance de loyer - ${data.formattedPeriod}</title>
</head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f5f5f5;">
  <div style="background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
    <div style="background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%); color: white; padding: 30px;">
      <h1 style="margin: 0; font-size: 24px;">Quittance de loyer</h1>
      <p style="margin: 5px 0 0 0; opacity: 0.9;">${data.formattedPeriod}</p>
    </div>

    <div style="padding: 30px;">
      <p style="margin: 0 0 20px 0;">Bonjour <strong>${data.tenantName}</strong>,</p>

      <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <p style="margin: 0 0 10px 0;"><strong>N° Quittance:</strong> ${data.receiptNumber}</p>
        <p style="margin: 0 0 10px 0;"><strong>Locataire:</strong> ${data.tenantName}</p>
        ${data.isProfessional ? `
        <div style="background: #edf2f7; padding: 10px 15px; border-radius: 4px; margin: 10px 0; font-size: 13px;">
          ${data.tenantSiret ? `<p style="margin: 0 0 5px 0;"><strong>SIRET:</strong> ${data.tenantSiret}</p>` : ""}
          ${data.tenantVatNumber ? `<p style="margin: 0;"><strong>N° TVA:</strong> ${data.tenantVatNumber}</p>` : ""}
        </div>
        ` : ""}
        <p style="margin: 0 0 10px 0;"><strong>Bien:</strong> ${data.propertyName}</p>
        <p style="margin: 0;"><strong>Adresse:</strong> ${data.propertyAddress}</p>
      </div>

      <p style="margin-bottom: 20px;">Veuillez trouver ci-joint votre quittance de loyer n° <strong>${data.receiptNumber}</strong> pour la période de <strong>${data.formattedPeriod}</strong>${data.dueDate ? `, à régler avant le <strong>${data.dueDate}</strong>` : ''}.</p>

      <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
        <tr style="border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 12px 0; color: #718096;">Loyer</td>
          <td style="padding: 12px 0; text-align: right; font-weight: 600;">${data.rentDue.toFixed(2)} €</td>
        </tr>
        <tr style="border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 12px 0; color: #718096;">Charges</td>
          <td style="padding: 12px 0; text-align: right; font-weight: 600;">${data.chargesDue.toFixed(2)} €</td>
        </tr>
        ${data.depositAdjustment ? `
        <tr style="border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 12px 0; color: #718096;">Ajustement du dépôt de garantie (non soumis à TVA)</td>
          <td style="padding: 12px 0; text-align: right; font-weight: 600;">${data.depositAdjustment.toFixed(2)} €</td>
        </tr>` : ''}
        <tr style="background: #f0f9ff;">
          <td style="padding: 16px 0; font-size: 18px; font-weight: bold; color: #1e3a5f;">Total à régler (TTC)</td>
          <td style="padding: 16px 0; text-align: right; font-size: 18px; font-weight: bold; color: #1e3a5f;">${data.totalDue.toFixed(2)} €</td>
        </tr>
      </table>

      ${data.hasIndexationAnnex ? `
      <div style="background: #eaf1f8; border-left: 3px solid #1f4e79; padding: 12px 15px; margin: 20px 0;">
        <p style="margin: 0;"><strong>Révision annuelle du loyer</strong> : le détail du calcul (indices, nouveau loyer, dépôt de garantie) figure dans l'annexe jointe.</p>
      </div>` : ''}

      ${data.sciIban ? `
      <div style="background: #f8f9fa; border: 1px solid #e2e8f0; padding: 15px 20px; border-radius: 8px; margin: 20px 0;">
        <p style="margin: 0 0 8px 0;"><strong>Règlement par virement bancaire</strong></p>
        <p style="margin: 0; font-family: monospace; font-size: 13px;">Titulaire : ${data.sciName}<br/>IBAN : ${data.sciIban.replace(/(.{4})/g, '$1 ').trim()}${data.sciBic ? `<br/>BIC : ${data.sciBic}` : ''}</p>
      </div>` : ""}

      <p style="margin: 20px 0;">Nous vous remercions par avance pour votre règlement.</p>

      <div style="background: #e8f4fd; border: 1px solid #bee3f8; padding: 15px; border-radius: 8px; margin: 20px 0;">
        <p style="margin: 0;"><strong>📎 Pièce jointe:</strong> Vous trouverez ci-joint votre quittance de loyer.</p>
      </div>

      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0;">
        <p style="margin: 0;"><strong>${data.sciName}</strong></p>
        ${data.sciAddress ? `<p style="color: #718096; font-size: 14px; margin: 5px 0;">${data.sciAddress}</p>` : ""}
        ${data.sciSiret ? `<p style="color: #718096; font-size: 14px; margin: 5px 0;">SIRET: ${data.sciSiret}</p>` : ""}
        ${data.signatureName ? `<p style="margin-top: 20px;"><em>Pour ${data.sciName},</em><br><strong>${data.signatureName}</strong></p>` : ""}
      </div>
    </div>

    <div style="background: #2d3748; color: #a0aec0; padding: 20px; font-size: 12px; text-align: center;">
      <p style="margin: 10px 0 0 0; color: #718096;">Document généré automatiquement le ${data.issueDate || new Date().toLocaleDateString("fr-FR")}</p>
    </div>
  </div>
</body>
</html>`;
}
